// ── Cost components (keys written by Finance.API engine) ─────────────────────
export const CostComponentKeys = {
  RawMaterial: 'RawMaterial',
  DirectLabor: 'DirectLabor',
  IndirectLabor: 'IndirectLabor',
  Depreciation: 'Depreciation',
  Maintenance: 'Maintenance',
  Consumables: 'Consumables',
  Packaging: 'Packaging',
  Waste: 'Waste',
  Scrap: 'Scrap',
  Energy: 'Energy',
  Water: 'Water',
  Inoptsa: 'Inoptsa',
  Bunker: 'Bunker',
  Steam: 'Steam',
  Chemicals: 'Chemicals',
  Rent: 'Rent',
  Others: 'Others',
} as const;

export type CostComponentKey = (typeof CostComponentKeys)[keyof typeof CostComponentKeys];

// English fallbacks; localized values live in the i18n JSON under the
// `Cost_Component_*` namespace and are looked up via `translateWF`.
export const COST_COMPONENT_LABELS: Readonly<Record<string, string>> = {
  RawMaterial: 'Raw Material',
  DirectLabor: 'Direct Labor',
  IndirectLabor: 'Indirect Labor',
  Depreciation: 'Depreciation',
  Maintenance: 'Maintenance',
  Consumables: 'Consumables',
  Packaging: 'Packaging',
  Waste: 'Waste',
  Scrap: 'Scrap',
  Energy: 'Energy',
  Water: 'Water',
  Inoptsa: 'Inoptsa',
  Bunker: 'Bunker',
  Steam: 'Steam',
  Chemicals: 'Chemicals',
  Rent: 'Rent',
  Others: 'Others',
};

export const ALL_COST_COMPONENTS: CostComponentKey[] = [
  CostComponentKeys.RawMaterial,
  CostComponentKeys.DirectLabor,
  CostComponentKeys.IndirectLabor,
  CostComponentKeys.Depreciation,
  CostComponentKeys.Maintenance,
  CostComponentKeys.Consumables,
  CostComponentKeys.Packaging,
  CostComponentKeys.Waste,
  CostComponentKeys.Scrap,
  CostComponentKeys.Energy,
  CostComponentKeys.Water,
  CostComponentKeys.Inoptsa,
  CostComponentKeys.Bunker,
  CostComponentKeys.Steam,
  CostComponentKeys.Chemicals,
  CostComponentKeys.Rent,
  CostComponentKeys.Others,
];

// ── Cost columns (Raw Material + 6 production processes) ────────────────────
/**
 * Visual cost-column keys used by the Fabric Summary / Process Summary tabs.
 * Includes the six real production processes (Knitting → Rolling) plus a
 * RawMaterial column.
 *
 * RawMaterial is NOT a real process or cost center — yarn costs are booked
 * to the Knitting cost center alongside knitting labor / depreciation /
 * etc. The aggregator splits the `RawMaterial` line out of every entry's
 * costBreakdownUsd into a dedicated bucket so the UI can show it as its own
 * column. The reconciliation invariant still holds:
 * parent.totalCost == sum of (RawMaterial + 6 process buckets).
 */
export const ProcessKeys = {
  RawMaterial: 'RawMaterial',
  Knitting: 'Knitting',
  Greige: 'Greige',
  Batching: 'Batching',
  Dyeing: 'Dyeing',
  Finishing: 'Finishing',
  Rolling: 'Rolling',
} as const;

export type ProcessKey = (typeof ProcessKeys)[keyof typeof ProcessKeys];

/**
 * Display order in the Fabric / Process tables. RawMaterial sits leftmost
 * because yarn cost is the first-touch cost in the roll's lifecycle (consumed
 * at Knitting).
 */
export const ALL_PROCESSES: ProcessKey[] = [
  ProcessKeys.RawMaterial,
  ProcessKeys.Knitting,
  ProcessKeys.Greige,
  ProcessKeys.Batching,
  ProcessKeys.Dyeing,
  ProcessKeys.Finishing,
  ProcessKeys.Rolling,
];

/**
 * The real production processes (excludes RawMaterial). Used by views that
 * already break costs out by component (e.g. Component Summary), where
 * treating RawMaterial as both a row AND a column would render the same
 * number on the diagonal and zeros elsewhere.
 */
export const REAL_PROCESSES: ProcessKey[] = [
  ProcessKeys.Knitting,
  ProcessKeys.Greige,
  ProcessKeys.Batching,
  ProcessKeys.Dyeing,
  ProcessKeys.Finishing,
  ProcessKeys.Rolling,
];

/**
 * Cost-center code → process bucket. Matches Finance.Models.CostCenterLocationMap
 * on the backend. If the backend adds cost centers, mirror them here.
 */
export const COST_CENTER_TO_PROCESS: Readonly<Record<string, ProcessKey>> = {
  '1010102130': ProcessKeys.Knitting,
  '1010103001': ProcessKeys.Greige,
  '1010104007': ProcessKeys.Batching,
  '1010105019': ProcessKeys.Dyeing,
  '1010115001': ProcessKeys.Finishing,
  '1010114011': ProcessKeys.Rolling,
};

export const PROCESS_LABELS: Readonly<Record<ProcessKey, string>> = {
  RawMaterial: 'Raw Material',
  Knitting: 'Knitting',
  Greige: 'Greige',
  Batching: 'Batching',
  Dyeing: 'Dyeing',
  Finishing: 'Finishing',
  Rolling: 'Rolling',
};

// ── Cost-center inventory (drives calc-control + prerequisites grid) ─────────
export interface CostCenterInfo {
  code: string;
  process: ProcessKey;
  label: string;
}

export const ALL_COST_CENTERS: CostCenterInfo[] = [
  { code: '1010102130', process: ProcessKeys.Knitting, label: 'Knitting' },
  { code: '1010103001', process: ProcessKeys.Greige, label: 'Greige' },
  { code: '1010104007', process: ProcessKeys.Batching, label: 'Batching' },
  { code: '1010105019', process: ProcessKeys.Dyeing, label: 'Dyeing' },
  { code: '1010115001', process: ProcessKeys.Finishing, label: 'Finishing' },
  { code: '1010114011', process: ProcessKeys.Rolling, label: 'Rolling' },
];
